/**
 * ProjectBuilder — slim orchestrator.
 *
 * Responsibilities:
 *   • Load + save the project
 *   • Provide the single `update(patch)` mutator (consumed by every tab)
 *   • Single-step undo / redo with auto-persist
 *   • Routing between tabs + export buttons
 *
 * Heavy UI lives under `./project/*Tab.jsx` (one file per tab) and is loaded
 * synchronously here because the bundle is small enough to not need lazy
 * loading and we want fast tab switches.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, FloppyDisk, FilePdf, FileCode, Stack, ListBullets, Lightning,
  Sparkle, Calculator, Question, ArrowUUpLeft, ArrowUUpRight, Share, ChartLine,
} from "@phosphor-icons/react";
import Room3DView from "@/components/Room3DView";
import SmallPowerTab from "@/components/SmallPowerTab";
import ShareDialog from "@/components/ShareDialog";
import SpecTab from "./project/SpecTab";
import CircuitsTab from "./project/CircuitsTab";
import LibraryTab from "./project/LibraryTab";
import DiagramTab from "./project/DiagramTab";
import SizeTab from "./project/SizeTab";
import SizingTab from "./project/SizingTab";
import CablesTab from "./project/CablesTab";
import BomTab from "./project/BomTab";
import StudiesTab from "./project/StudiesTab";
import InsightsWidget from "./project/InsightsWidget";
import HelpTab from "./project/HelpTab";
import AssistantPanel from "./project/AssistantPanel";

const ALL_TABS = [
  { id: "spec",         label: "Spec",         icon: Lightning },
  { id: "circuits",     label: "Circuits",     icon: ListBullets },
  { id: "library",      label: "Equipment",    icon: Stack },
  { id: "diagram",      label: "Single-Line",  icon: Sparkle },
  { id: "sizing",       label: "Sizing",       icon: Calculator },
  { id: "size",         label: "Size",         icon: Calculator },
  { id: "view3d",       label: "3D View",      icon: Sparkle },
  { id: "cables",       label: "Cables",       icon: ListBullets },
  { id: "small_power",  label: "Small Power",  icon: Lightning },
  { id: "bom",          label: "BOM",          icon: FilePdf },
  { id: "studies",      label: "Studies",      icon: ChartLine },
  { id: "help",         label: "Help",         icon: Question },
];

const TABS_BY_TYPE = {
  electrical_room: ["spec", "library", "size", "view3d", "cables", "small_power", "diagram", "sizing", "bom", "studies", "help"],
  default:         ["spec", "circuits", "library", "diagram", "sizing", "cables", "small_power", "bom", "studies", "help"],
};

function tabsFor(panelType, isAdmin) {
  const ids = TABS_BY_TYPE[panelType] || TABS_BY_TYPE.default;
  // v2.89 — Panel Design tabs (`size`, `view3d`) are the 2D/3D CAD
  // panel-layout editor + 3D room view. Parked from PE/Client during
  // the platform pivot; still visible to admin for continued dev.
  const PANEL_DESIGN_TAB_IDS = new Set(["size", "view3d"]);
  return ids
    .filter((id) => isAdmin || !PANEL_DESIGN_TAB_IDS.has(id))
    .map((id) => ALL_TABS.find((t) => t.id === id))
    .filter(Boolean);
}

/**
 * v1.85 — Field-set-agnostic PATCH body builder.
 *
 * Previously this was a hand-maintained 22-field whitelist. Every time a new
 * top-level Project field landed in the backend Pydantic schema (e.g. v1.84's
 * `room_partitions`), the whitelist had to be updated by hand — and forgetting
 * meant the field SILENTLY failed to persist (iter_58 caught one such bug).
 *
 * The new approach: shallow-copy `snap` minus a small read-only blacklist. The
 * backend's `ProjectUpdate` Pydantic v2 model (server.py L172) ignores
 * unknown fields by default (`extra="ignore"`), so any UI-only computed state
 * that happens to live on the project object is dropped silently. The handler
 * then strips `None` values (server.py L978: `if v is not None`), so no
 * accidental nulling-out of unset fields. Net effect: ANY future
 * `ProjectUpdate` field becomes savable with zero frontend changes.
 *
 * The matching round-trip regression test lives at
 * `/app/backend/tests/test_project_roundtrip.py` — it patches a project with
 * a value for every writable field and asserts each round-trips.
 */
const READ_ONLY_PROJECT_FIELDS = new Set([
  "id", "_id",
  "owner_id", "owner_email", "user_id", "organization_id",
  "created_at", "updated_at",
  // server-derived caches the UI may surface but the user can't directly set.
  "permissions", "role",
]);
function snapshotBody(snap) {
  if (!snap || typeof snap !== "object") return {};
  const body = {};
  for (const [k, v] of Object.entries(snap)) {
    if (READ_ONLY_PROJECT_FIELDS.has(k)) continue;
    if (v === undefined) continue;
    body[k] = v;
  }
  return body;
}

export default function ProjectBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh: refreshUser } = useAuth();
  // Per-user autosave preference, default ON. Persisted via /auth/me/preferences.
  const autosaveEnabled = user?.preferences?.autosave_enabled !== false;
  const [project, setProject] = useState(null);
  // Restore the last-viewed tab for this project (session-scoped).
  const tabKey = `eengdes:lastTab:${id}`;
  const scrollKey = (t) => `eengdes:scroll:${id}:${t}`;
  const initialTab = (() => {
    try { return sessionStorage.getItem(tabKey) || "spec"; } catch { return "spec"; }
  })();
  const [tab, setTab] = useState(initialTab);
  // Track which tabs have ever been visited — once mounted, they stay
  // mounted (display:none-hidden) so their internal state (Room3DView
  // camera + tray selection, Library filters, etc.) survives tab switches.
  const [visited, setVisited] = useState(() => new Set([initialTab]));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const tabScrollRef = useRef(null);

  // Persist tab choice. Restore scroll for incoming tab.
  useEffect(() => {
    try { sessionStorage.setItem(tabKey, tab); } catch {}
    // Restore scroll on next paint so the tab body has mounted.
    requestAnimationFrame(() => {
      const sav = (() => { try { return sessionStorage.getItem(scrollKey(tab)); } catch { return null; } })();
      if (sav != null && tabScrollRef.current) {
        tabScrollRef.current.scrollTop = Number(sav) || 0;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id]);

  // Save scroll on every scroll (throttled via rAF), and on unmount —
  // so navigating away and back lands on the same spot.
  useEffect(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        try { sessionStorage.setItem(scrollKey(tab), String(el.scrollTop || 0)); } catch {}
        raf = null;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      // NOTE: do NOT re-save in cleanup. By the time it runs, the previous
      // tab has been hidden (display:none on its body) and the shared scroll
      // container's scrollHeight has shrunk to fit the NEW tab — the browser
      // has already clamped scrollTop to 0, so saving here would clobber the
      // correct value that switchTab() persisted moments earlier.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id]);

  // Wrap setTab so we save scroll before switching AND mark the new tab as
  // visited (so it gets mounted and stays mounted on subsequent switches).
  const switchTab = (nextTab) => {
    if (tabScrollRef.current) {
      try { sessionStorage.setItem(scrollKey(tab), String(tabScrollRef.current.scrollTop || 0)); } catch {}
    }
    setVisited((v) => (v.has(nextTab) ? v : new Set([...v, nextTab])));
    setTab(nextTab);
  };

  // Single-step undo / redo
  const undoRef     = useRef(null);
  const redoRef     = useRef(null);
  const isReverting = useRef(false);
  const [, setHistoryTick] = useState(0);
  const canUndo = !!undoRef.current;
  const canRedo = !!redoRef.current;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setProject(data);
      } catch {
        toast.error("Project not found");
        nav("/dashboard");
      }
    })();
  }, [id, nav]);

  const update = (patch) => {
    setProject((p) => {
      if (!isReverting.current && p) {
        undoRef.current = p;
        redoRef.current = null;
        setHistoryTick((t) => t + 1);
      }
      return { ...p, ...patch };
    });
    setDirty(true);
  };

  /**
   * v1.86b — Explicit-clear path. The autosave PATCH filters `if v is not None`
   * on the backend, so simply setting a field to `null` in the project object
   * leaves the persisted value intact. `clear()` bypasses that by sending the
   * `clear_fields` sentinel (server.py L979) which the backend allowed-list-
   * gates to ProjectUpdate field names. Returns the API promise so callers
   * can await + toast on completion. Local state mirrors the clear so the UI
   * updates immediately.
   */
  const clear = async (...fieldNames) => {
    const fields = fieldNames.flat().filter((f) => typeof f === "string" && f);
    if (!fields.length) return;
    // Optimistic local update.
    setProject((p) => p ? { ...p, ...Object.fromEntries(fields.map((f) => [f, null])) } : p);
    try {
      await api.patch(`/projects/${id}`, { clear_fields: fields });
      setDirty(false);
    } catch {
      toast.error("Could not clear " + fields.join(", "));
    }
  };

  const _persistSnapshot = async (snapshot) => {
    try {
      await api.patch(`/projects/${id}`, snapshotBody(snapshot));
      setDirty(false);
    } catch (err) {
      // Surface tag-uniqueness violations explicitly — they're user-actionable
      // (rename the offending item) and otherwise look like an opaque save
      // failure on the auto-save debounce.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "DUPLICATE_TAG") {
        toast.error(detail.message || "Tag conflict — pick a unique tag");
        return;
      }
      toast.error("Couldn't persist — please save manually");
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Auto-save (debounced) — when enabled in user preferences, every
  // local edit triggers a save 1.5 s after the last keystroke / drag.
  // When disabled, we put a `beforeunload` handler in place so the
  // user gets the browser's native "Leave site?" prompt if they try
  // to close the tab with unsaved changes.
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!autosaveEnabled || !dirty || !project) return undefined;
    const t = setTimeout(() => { _persistSnapshot(project); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, dirty, autosaveEnabled, id]);

  useEffect(() => {
    if (autosaveEnabled || !dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Modern browsers ignore the message and show their own native
      // string, but setting returnValue is what triggers the prompt.
      e.returnValue = "You have unsaved changes — leave anyway?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [autosaveEnabled, dirty]);

  // Toggle auto-save in user preferences (persisted server-side).
  const toggleAutosave = async () => {
    const next = !autosaveEnabled;
    try {
      // Optimistic — flip immediately so the indicator updates without lag.
      await api.patch("/auth/me/preferences", { autosave_enabled: next });
      await refreshUser?.();
      toast.success(next ? "Auto-save on" : "Auto-save off (you'll need to Save manually)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update preference");
    }
  };

  const doUndo = () => {
    if (!undoRef.current) { toast.info("Nothing to undo"); return; }
    const prev = undoRef.current;
    isReverting.current = true;
    setProject((current) => {
      redoRef.current = current;
      undoRef.current = null;
      return prev;
    });
    setHistoryTick((t) => t + 1);
    setDirty(true);
    Promise.resolve().then(() => _persistSnapshot(prev)).finally(() => { isReverting.current = false; });
    toast.success("Undid last change", { duration: 1500 });
  };

  const doRedo = () => {
    if (!redoRef.current) { toast.info("Nothing to redo"); return; }
    const next = redoRef.current;
    isReverting.current = true;
    setProject((current) => {
      undoRef.current = current;
      redoRef.current = null;
      return next;
    });
    setHistoryTick((t) => t + 1);
    setDirty(true);
    Promise.resolve().then(() => _persistSnapshot(next)).finally(() => { isReverting.current = false; });
    toast.success("Redid change", { duration: 1500 });
  };

  // Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z — skip when typing.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = (t?.tagName || "").toUpperCase();
      const inField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable;
      if (inField) return;
      const k = (e.key || "").toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (k === "z" && !e.shiftKey) { e.preventDefault(); doUndo(); }
      else if (k === "y" || (k === "z" && e.shiftKey)) { e.preventDefault(); doRedo(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [id]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/projects/${id}`, snapshotBody(project));
      setProject(data);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const exportFile = async (kind) => {
    if (dirty) await save();
    const url = `${API}/projects/${id}/export/${kind}`;
    try {
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        if (resp.status === 501) {
          const err = await resp.json().catch(() => ({}));
          toast.error(err.detail || "Not available yet");
          return;
        }
        throw new Error("export failed");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${project.name.replace(/[^A-Za-z0-9_-]+/g, "_")}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${kind.toUpperCase()} exported`);
    } catch {
      toast.error(`Failed to export ${kind.toUpperCase()}`);
    }
  };

  if (!project) {
    return (
      <div className="industrial min-h-screen bg-[#0A0C10]">
        <Topbar active="dashboard" />
        <div className="p-12 text-gray-400 font-mono">Loading project...</div>
      </div>
    );
  }

  return (
    <div className="industrial min-h-screen bg-[#0A0C10] flex flex-col">
      <Topbar active="dashboard" />

      {/* Project header */}
      <div className="border-b border-white/10 bg-[#0d1015] sticky top-16 z-30">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button onClick={() => nav("/dashboard")} className="text-gray-400 hover:text-white" data-testid="back-to-dashboard" title="Back">
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="label-tag">{project.code_standard} · {project.panel_type.replace(/_/g, " ")}</div>
              <div className="font-display text-xl font-bold" data-testid="project-name-header">{project.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={doUndo} disabled={!canUndo} className="btn-ghost text-sm py-2 inline-flex items-center gap-2 disabled:opacity-30" title="Undo last change (Ctrl+Z)" data-testid="undo-btn">
              <ArrowUUpLeft size={16} /> Undo
            </button>
            <button onClick={doRedo} disabled={!canRedo} className="btn-ghost text-sm py-2 inline-flex items-center gap-2 disabled:opacity-30" title="Redo (Ctrl+Y)" data-testid="redo-btn">
              <ArrowUUpRight size={16} /> Redo
            </button>
            <button onClick={() => setShareOpen(true)} className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="share-btn" title="Create a public read-only link for this project">
              <Share size={16} /> Share
            </button>
            <button onClick={() => exportFile("dxf")} className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="export-dxf">
              <FileCode size={16} /> DXF
            </button>
            <button onClick={() => exportFile("dwg")} className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="export-dwg" title="True DWG via Autodesk APS — requires APS keys">
              <FileCode size={16} /> DWG
            </button>
            <button onClick={() => exportFile("pdf")} className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="export-pdf">
              <FilePdf size={16} /> PDF
            </button>
            <button
              onClick={toggleAutosave}
              className={`btn-ghost text-xs py-2 inline-flex items-center gap-1 ${
                autosaveEnabled ? "!text-emerald-300 !border-emerald-500/30" : "!text-gray-400"
              }`}
              title={autosaveEnabled
                ? "Auto-save is on — every edit persists 1.5 s after the last change."
                : "Auto-save is off — close warning will appear if you have unsaved changes."}
              data-testid="toggle-autosave">
              {autosaveEnabled ? "Auto-save on" : "Auto-save off"}
            </button>
            <button onClick={save} disabled={saving || !dirty} className="btn-primary text-sm py-2 inline-flex items-center gap-2" data-testid="save-project">
              <FloppyDisk size={16} weight="fill" /> {dirty ? "Save" : "Saved"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 md:px-10 flex items-center gap-1 overflow-x-auto">
          {tabsFor(project.panel_type, user?.role === "admin").map((t) => (
            <button key={t.id} onClick={() => switchTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-2 whitespace-nowrap ${tab === t.id ? "border-[#00E5FF] text-white" : "border-transparent text-gray-500 hover:text-white"}`}
              data-testid={`tab-${t.id}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_360px]">
        <div
          ref={tabScrollRef}
          className="p-6 md:p-10 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 168px)" }}
          data-testid="tab-scroll-container"
        >
          {/* Each tab is mounted on FIRST visit and kept mounted afterwards
              (hidden via display:none) so its internal state — Room3DView
              camera + tray selection, Library filters, Cables segment state,
              etc. — survives tab switches. */}
          {visited.has("spec")        && <div style={{ display: tab === "spec"        ? "block" : "none" }}>
            <div className="mb-6"><InsightsWidget project={project} /></div>
            <SpecTab project={project} update={update} clear={clear} />
          </div>}
          {visited.has("circuits")    && <div style={{ display: tab === "circuits"    ? "block" : "none" }}><CircuitsTab project={project} update={update} projectId={id} /></div>}
          {visited.has("library")     && <div style={{ display: tab === "library"     ? "block" : "none" }}><LibraryTab project={project} update={update} projectId={id} /></div>}
          {visited.has("diagram")     && <div style={{ display: tab === "diagram"     ? "block" : "none" }}><DiagramTab project={project} /></div>}
          {visited.has("sizing")      && <div style={{ display: tab === "sizing"      ? "block" : "none" }}><SizingTab project={project} /></div>}
          {visited.has("size")        && <div style={{ display: tab === "size"        ? "block" : "none" }}><SizeTab project={project} update={update} onUndo={doUndo} onRedo={doRedo} canUndo={canUndo} canRedo={canRedo} /></div>}
          {visited.has("view3d")      && <div style={{ display: tab === "view3d"      ? "block" : "none" }}><Room3DView project={project} update={update} projectId={id} onJumpToTab={switchTab} /></div>}
          {visited.has("cables")      && <div style={{ display: tab === "cables"      ? "block" : "none" }}><CablesTab projectId={id} project={project} update={update} /></div>}
          {visited.has("small_power") && <div style={{ display: tab === "small_power" ? "block" : "none" }}><SmallPowerTab project={project} projectId={id} update={update} /></div>}
          {visited.has("bom")         && <div style={{ display: tab === "bom"         ? "block" : "none" }}><BomTab projectId={id} project={project} update={update} /></div>}
          {visited.has("studies")     && <div style={{ display: tab === "studies"     ? "block" : "none" }}><StudiesTab projectId={id} project={project} update={update} /></div>}
          {tab === "help"     && <HelpTab />}
        </div>
        <AssistantPanel projectId={id} />
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} projectId={id} />
    </div>
  );
}
